<template>
  <div class="resume">
    <!-- ===== 头部 ===== -->
    <section class="r-hero grid-bg">
      <div class="glow-orb" style="background: var(--accent); top: -80px; right: 5%; width: 420px; height: 420px; opacity: 0.16;"></div>
      <div class="container r-hero-inner">
        <div class="r-hero-left" data-aos="fade-up">
          <div class="r-avatar">
            <img :src="avatar" alt="余静头像" class="r-avatar-img" />
            <div class="r-avatar-ring"></div>
          </div>
          <span class="r-alias">{{ resume.alias }}</span>
          <span class="r-workyears">{{ resume.workYears }} 经验</span>
        </div>
        <div class="r-hero-right" data-aos="fade-up" data-aos-delay="80">
          <span class="section-eyebrow">RESUME</span>
          <h1 class="r-name">{{ resume.name }}</h1>
          <p class="r-title">{{ resume.title }}</p>
          <p class="r-intro">{{ resume.intro }}</p>

          <div class="r-info-grid">
            <div class="r-info"><i class="fa-solid fa-cake-candles"></i><span>{{ resume.age }}</span></div>
            <div class="r-info"><i class="fa-solid fa-briefcase"></i><span>{{ resume.workYears }}工作经验</span></div>
            <div class="r-info"><i class="fa-solid fa-location-dot"></i><span>{{ resume.location }}</span></div>
            <div class="r-info"><i class="fa-solid fa-phone"></i><span>{{ resume.phone }}</span></div>
            <div class="r-info"><i class="fa-solid fa-envelope"></i><span>{{ resume.email }}</span></div>
            <div class="r-info"><i class="fa-solid fa-graduation-cap"></i><span>本科 · 计算机科学与技术</span></div>
          </div>

          <div class="r-traits">
            <span v-for="t in resume.traits" :key="t" class="r-trait">{{ t }}</span>
          </div>
        </div>
      </div>

      <!-- 项目统计条 -->
      <div class="container r-stats-bar" data-aos="fade-up" data-aos-delay="160">
        <div v-for="s in projectStats" :key="s.label" class="r-stat">
          <span class="r-stat-num">{{ s.value }}<span class="r-stat-suffix">{{ s.suffix }}</span></span>
          <span class="r-stat-label">{{ s.label }}</span>
        </div>
      </div>
    </section>

    <!-- ===== 业务领域概览 ===== -->
    <section class="section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">DOMAINS</span>
          <h2 class="section-title">业务领域</h2>
          <p class="section-subtitle">横跨烟草行业全链路、物联网回收生态、开源工具与全栈拓展四大领域</p>
        </div>
        <div class="domain-grid">
          <div v-for="(d, i) in domains" :key="d.title" class="domain-card" data-aos="fade-up" :data-aos-delay="i * 70">
            <div class="domain-icon" :style="{ color: d.color, background: d.color + '14', borderColor: d.color + '44' }">
              <i :class="d.icon"></i>
            </div>
            <h3 class="domain-title">{{ d.title }}</h3>
            <p class="domain-desc">{{ d.desc }}</p>
            <div class="domain-tags">
              <span v-for="t in d.tags" :key="t" class="domain-tag">{{ t }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 技能矩阵 ===== -->
    <section class="section skills-section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">SKILLS</span>
          <h2 class="section-title">技能矩阵</h2>
          <p class="section-subtitle">{{ resume.workYears }}沉淀的多领域技术能力，移动端、硬件、后端、底层全覆盖</p>
        </div>

        <div class="skills-layout">
          <div class="radar-box" data-aos="fade-up" data-aos-delay="80">
            <div ref="radarEl" class="radar-chart"></div>
          </div>
          <div class="skill-groups">
            <div v-for="(g, i) in resume.skillGroups" :key="g.group" class="skill-group" data-aos="fade-up" :data-aos-delay="i * 60">
              <div class="sg-head">
                <i :class="g.icon" class="sg-icon"></i>
                <h3 class="sg-title">{{ g.group }}</h3>
              </div>
              <div class="sg-list">
                <div v-for="s in g.skills" :key="s.name" class="sg-item">
                  <div class="sg-item-head">
                    <span class="sg-name">{{ s.name }}</span>
                    <span class="sg-level">{{ s.level }}%</span>
                  </div>
                  <div class="sg-bar"><div class="sg-bar-fill" :style="{ width: s.level + '%' }"></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 工作经历 ===== -->
    <section class="section exp-section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">EXPERIENCE</span>
          <h2 class="section-title">工作经历</h2>
          <p class="section-subtitle">{{ resume.workYears }}深耕 Android 与 Java 开发，移动端负责人</p>
        </div>
        <div class="exp-timeline">
          <div v-for="(e, i) in resume.experiences" :key="i" class="exp-item" data-aos="fade-up" :data-aos-delay="i * 80">
            <div class="exp-dot"></div>
            <div class="exp-card">
              <div class="exp-time">{{ e.time }}</div>
              <h3 class="exp-company">{{ e.company }}</h3>
              <div class="exp-role">{{ e.role }}</div>
              <ul class="exp-duties">
                <li v-for="(d, di) in e.duties" :key="di">{{ d }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 项目经验 ===== -->
    <section class="section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">PROJECTS</span>
          <h2 class="section-title">项目经验</h2>
          <p class="section-subtitle">中国烟草西南大区核心项目 + 物联网回收生态 + ESP32 + 小程序，部分项目长期运维至今</p>
        </div>
        <div class="proj-list">
          <div v-for="(p, i) in resume.portfolioProjects" :key="p.name" class="pp-card" data-aos="fade-up" :data-aos-delay="(i % 3) * 60">
            <div class="pp-head">
              <div class="pp-time">{{ p.time }}</div>
              <div class="pp-role">{{ p.role }}</div>
            </div>
            <h3 class="pp-name">{{ p.name }}</h3>
            <div class="pp-tags">
              <span v-for="t in p.tags" :key="t" class="pp-tag">{{ t }}</span>
            </div>
            <p class="pp-desc">{{ p.desc }}</p>
            <div class="pp-meta">
              <div class="pp-meta-row">
                <span class="pp-meta-label"><i class="fa-solid fa-laptop-code"></i> 开发环境</span>
                <span class="pp-meta-val">{{ p.env }}</span>
              </div>
              <div class="pp-meta-row">
                <span class="pp-meta-label"><i class="fa-solid fa-wrench"></i> 采用技术</span>
                <span class="pp-meta-val">{{ p.tech }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 教育与自我评价 ===== -->
    <section class="section edu-section">
      <div class="container">
        <div class="edu-grid">
          <div class="edu-card" data-aos="fade-up">
            <span class="section-eyebrow">EDUCATION</span>
            <h2 class="section-title">教育背景</h2>
            <div class="edu-block">
              <div class="edu-time">{{ resume.education.time }}</div>
              <h3 class="edu-school">{{ resume.education.school }}</h3>
              <div class="edu-major">{{ resume.education.major }}</div>
              <div class="edu-courses">
                <span class="edu-courses-label">主修课程</span>
                <p>{{ resume.education.courses }}</p>
              </div>
            </div>
          </div>
          <div class="edu-card" data-aos="fade-up" data-aos-delay="80">
            <span class="section-eyebrow">ABOUT</span>
            <h2 class="section-title">自我评价</h2>
            <p class="assess-text">{{ resume.selfAssessment }}</p>
            <div class="assess-traits">
              <span v-for="t in ['稳重', '负责', '真诚', '善学', '协作', '适应力']" :key="t" class="assess-tag">{{ t }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 联系方式 ===== -->
    <section class="section contact-section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">CONTACT</span>
          <h2 class="section-title">联系方式</h2>
          <p class="section-subtitle">技术交流、项目合作、招聘沟通，欢迎随时联系</p>
        </div>
        <div class="contact-grid">
          <a v-for="(c, i) in resume.contacts" :key="c.label" :href="c.url" target="_blank" rel="noopener" class="contact-card" data-aos="fade-up" :data-aos-delay="(i % 3) * 60">
            <div class="contact-icon"><i :class="c.icon"></i></div>
            <div class="contact-text">
              <div class="contact-label">{{ c.label }}</div>
              <div class="contact-value">{{ c.value }}</div>
            </div>
            <i class="fa-solid fa-arrow-up-right-from-square contact-ext"></i>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import * as echarts from 'echarts/core'
import { RadarChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import { TooltipComponent, LegendComponent } from 'echarts/components'
import { resume, projectStats } from '@/data/resume.js'
import avatar from '@/assets/avatar.png'

echarts.use([RadarChart, CanvasRenderer, TooltipComponent, LegendComponent])

const radarEl = ref(null)

const domains = [
  {
    title: '烟草行业全链路',
    icon: 'fa-solid fa-leaf',
    color: '#34D399',
    desc: '中国烟草西南大区（凉山/攀枝花）烟叶收购仓储全流程：排号→交售刷卡→定级→磅码→打包→扫码→预检预约→二检→入库→出库→盘点→运维。',
    tags: ['20+ 项目', '7年+ 运维', '凉山/攀枝花', '多地区分仓']
  },
  {
    title: '物联网回收生态',
    icon: 'fa-solid fa-recycle',
    color: '#38BDF8',
    desc: '「大卫智能」垃圾分类回收全栈：Spring Boot 后端 + Android 清运端 + 微信小程序用户端 + ESP32 蓝牙电子秤，MQTT 设备控制与 OTA 升级。',
    tags: ['Spring Boot', 'MQTT', '小程序', 'ESP32', 'OTA']
  },
  {
    title: '开源工具生态',
    icon: 'fa-solid fa-code-branch',
    color: '#818CF8',
    desc: '四大 Android 开源工具库原创作者：kotlinx 扩展方法、YSerialPort 串口通信、yutils 工具集合、yuvc UVC 摄像头，均发布至 Maven 中央仓库。',
    tags: ['kotlinx', 'YSerialPort', 'yutils', 'yuvc', 'Maven Central']
  },
  {
    title: '全栈技术拓展',
    icon: 'fa-solid fa-layer-group',
    color: '#22D3EE',
    desc: '从 Android 原生拓展至 ESP32 固件（Arduino/PlatformIO）、微信小程序（原生+Vant）、Spring Boot 后端、Vue 前端，覆盖软硬件全栈。',
    tags: ['ESP32', '小程序', 'Spring Boot', 'Vue', 'NDK/JNI']
  }
]

onMounted(async () => {
  await nextTick()
  if (radarEl.value) {
    const chart = echarts.init(radarEl.value, null, { renderer: 'canvas' })
    const indicators = resume.skillGroups.map(g => ({ name: g.group, max: 100 }))
    const values = resume.skillGroups.map(g => Math.round(g.skills.reduce((s, x) => s + x.level, 0) / g.skills.length))
    chart.setOption({
      tooltip: { trigger: 'item', backgroundColor: '#1C2330', borderColor: '#2A3441', textStyle: { color: '#E6EDF3' } },
      radar: {
        indicator: indicators,
        shape: 'polygon',
        splitNumber: 4,
        axisName: { color: '#8B949E', fontSize: 12 },
        splitLine: { lineStyle: { color: '#2A3441' } },
        splitArea: { areaStyle: { color: ['rgba(255,255,255,0.01)', 'rgba(255,255,255,0.03)'] } },
        axisLine: { lineStyle: { color: '#2A3441' } }
      },
      series: [{
        type: 'radar',
        data: [{
          value: values,
          name: '能力分布',
          areaStyle: { color: 'rgba(56, 189, 248, 0.22)' },
          lineStyle: { color: '#38BDF8', width: 2 },
          itemStyle: { color: '#38BDF8' }
        }]
      }]
    })
    window.addEventListener('resize', () => chart.resize())
  }
})
</script>

<style scoped>
/* ===== 头部 ===== */
.r-hero {
  position: relative;
  padding: 72px 0 0;
  overflow: hidden;
  background: linear-gradient(180deg, var(--bg-surface), var(--bg-base));
  border-bottom: 1px solid var(--border-soft);
}
.r-hero-inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 56px;
  align-items: center;
  padding-bottom: 56px;
}
.r-hero-left { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.r-avatar {
  position: relative;
  width: 130px;
  aspect-ratio: 112 / 148;
  display: grid; place-items: center;
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 16px 50px rgba(56, 189, 248, 0.3);
}
.r-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.r-avatar-ring {
  position: absolute;
  inset: -8px;
  border: 1px dashed var(--accent);
  border-radius: 32px;
  opacity: 0.5;
  animation: float 4s ease-in-out infinite;
}
.r-alias { font-family: var(--font-mono); font-size: 0.9rem; color: var(--text-muted); letter-spacing: 0.1em; }
.r-workyears {
  font-family: var(--font-mono);
  font-size: 0.82rem;
  color: var(--accent);
  padding: 3px 12px;
  border: 1px solid var(--accent);
  border-radius: 100px;
  background: var(--accent-soft);
}
.r-name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(3rem, 7vw, 5rem);
  line-height: 1;
  letter-spacing: -0.03em;
  margin: 8px 0 10px;
}
.r-title { font-family: var(--font-mono); font-size: 1.1rem; color: var(--accent); margin-bottom: 22px; }
.r-intro { color: var(--text-muted); font-size: 1.02rem; line-height: 1.8; max-width: 680px; margin-bottom: 28px; }
.r-info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px 28px; margin-bottom: 26px; max-width: 620px; }
.r-info { display: flex; align-items: center; gap: 10px; color: var(--text-muted); font-size: 0.92rem; }
.r-info i { color: var(--accent); width: 16px; }
.r-traits { display: flex; flex-wrap: wrap; gap: 8px; }
.r-trait {
  padding: 5px 12px;
  border: 1px solid var(--border);
  border-radius: 100px;
  font-size: 0.78rem;
  color: var(--text-muted);
  background: var(--bg-surface);
}

/* 统计条 */
.r-stats-bar {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--border-soft);
  border-top: 1px solid var(--border-soft);
  border-radius: 0;
}
.r-stat {
  background: var(--bg-surface);
  padding: 28px 20px;
  text-align: center;
}
.r-stat-num {
  font-family: var(--font-mono);
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--accent);
  line-height: 1;
  display: block;
  margin-bottom: 8px;
}
.r-stat-suffix { font-size: 1.1rem; }
.r-stat-label { font-size: 0.85rem; color: var(--text-muted); }

/* ===== 业务领域 ===== */
.domain-grid { margin-top: 56px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px; }
.domain-card {
  background: var(--bg-surface);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius);
  padding: 30px 28px;
  transition: all 0.3s ease;
}
.domain-card:hover { border-color: var(--border); transform: translateY(-4px); }
.domain-icon { width: 52px; height: 52px; display: grid; place-items: center; border-radius: 13px; font-size: 1.35rem; border: 1px solid var(--border); margin-bottom: 18px; }
.domain-title { font-family: var(--font-display); font-weight: 600; font-size: 1.3rem; margin-bottom: 10px; }
.domain-desc { color: var(--text-muted); font-size: 0.94rem; line-height: 1.75; margin-bottom: 16px; }
.domain-tags { display: flex; flex-wrap: wrap; gap: 7px; }
.domain-tag {
  font-family: var(--font-mono);
  font-size: 0.74rem;
  padding: 3px 10px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-soft);
  border-radius: 100px;
  color: var(--text-muted);
}

/* ===== 技能 ===== */
.skills-section { border-top: 1px solid var(--border-soft); }
.skills-layout { margin-top: 56px; display: grid; grid-template-columns: 1fr 1.4fr; gap: 32px; align-items: start; }
.radar-box { background: var(--bg-surface); border: 1px solid var(--border-soft); border-radius: var(--radius); padding: 24px; position: sticky; top: 96px; }
.radar-chart { width: 100%; height: 420px; }
.skill-groups { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.skill-group { background: var(--bg-surface); border: 1px solid var(--border-soft); border-radius: var(--radius); padding: 22px; transition: all 0.25s ease; }
.skill-group:hover { border-color: var(--border); transform: translateY(-3px); }
.sg-head { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.sg-icon { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 8px; background: var(--accent-soft); color: var(--accent); font-size: 0.95rem; }
.sg-title { font-family: var(--font-display); font-weight: 600; font-size: 1.05rem; }
.sg-list { display: flex; flex-direction: column; gap: 13px; }
.sg-item-head { display: flex; justify-content: space-between; font-size: 0.85rem; margin-bottom: 6px; }
.sg-name { color: var(--text); }
.sg-level { font-family: var(--font-mono); color: var(--text-dim); }
.sg-bar { height: 5px; background: var(--bg-elevated); border-radius: 3px; overflow: hidden; }
.sg-bar-fill { height: 100%; background: linear-gradient(90deg, var(--accent), var(--lime)); border-radius: 3px; transition: width 1s ease; }

/* ===== 工作经历 ===== */
.exp-section { border-top: 1px solid var(--border-soft); }
.exp-timeline { margin-top: 56px; position: relative; padding-left: 32px; }
.exp-timeline::before { content: ''; position: absolute; left: 7px; top: 8px; bottom: 8px; width: 1px; background: linear-gradient(180deg, var(--accent), var(--border)); }
.exp-item { position: relative; padding-bottom: 36px; }
.exp-item:last-child { padding-bottom: 0; }
.exp-dot { position: absolute; left: -32px; top: 8px; width: 15px; height: 15px; border-radius: 50%; background: var(--accent); border: 2px solid var(--bg-base); box-shadow: 0 0 0 4px var(--accent-soft); }
.exp-card { background: var(--bg-surface); border: 1px solid var(--border-soft); border-radius: 12px; padding: 24px 28px; }
.exp-time { font-family: var(--font-mono); font-size: 0.82rem; color: var(--accent); margin-bottom: 6px; }
.exp-company { font-family: var(--font-display); font-weight: 600; font-size: 1.2rem; margin-bottom: 4px; }
.exp-role { color: var(--text-muted); font-size: 0.92rem; margin-bottom: 14px; }
.exp-duties { display: flex; flex-direction: column; gap: 8px; }
.exp-duties li { position: relative; padding-left: 18px; color: var(--text-muted); font-size: 0.9rem; line-height: 1.65; }
.exp-duties li::before { content: ''; position: absolute; left: 0; top: 10px; width: 6px; height: 6px; background: var(--accent); border-radius: 50%; }

/* ===== 项目经验 ===== */
.proj-list { margin-top: 56px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px; }
.pp-card { background: var(--bg-surface); border: 1px solid var(--border-soft); border-radius: var(--radius); padding: 28px 30px; transition: all 0.3s ease; }
.pp-card:hover { border-color: var(--border); transform: translateY(-4px); }
.pp-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 10px; }
.pp-time { font-family: var(--font-mono); font-size: 0.8rem; color: var(--accent); }
.pp-role { font-size: 0.74rem; padding: 3px 10px; background: var(--accent-soft); color: var(--accent); border-radius: 100px; font-weight: 600; }
.pp-name { font-family: var(--font-display); font-weight: 600; font-size: 1.25rem; margin-bottom: 12px; line-height: 1.3; }
.pp-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.pp-tag { font-family: var(--font-mono); font-size: 0.7rem; padding: 2px 9px; background: var(--bg-elevated); border: 1px solid var(--border-soft); border-radius: 4px; color: var(--lime); }
.pp-desc { color: var(--text-muted); line-height: 1.75; margin-bottom: 18px; font-size: 0.92rem; }
.pp-meta { display: flex; flex-direction: column; gap: 10px; padding-top: 16px; border-top: 1px solid var(--border-soft); }
.pp-meta-row { display: flex; gap: 14px; font-size: 0.82rem; }
.pp-meta-label { flex-shrink: 0; color: var(--text-dim); display: inline-flex; align-items: center; gap: 6px; min-width: 92px; }
.pp-meta-label i { color: var(--accent); }
.pp-meta-val { color: var(--text-muted); font-family: var(--font-mono); font-size: 0.78rem; }

/* ===== 教育与评价 ===== */
.edu-section { border-top: 1px solid var(--border-soft); }
.edu-grid { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.edu-card { background: var(--bg-surface); border: 1px solid var(--border-soft); border-radius: var(--radius); padding: 36px 34px; }
.edu-block { margin-top: 24px; }
.edu-time { font-family: var(--font-mono); font-size: 0.82rem; color: var(--accent); margin-bottom: 8px; }
.edu-school { font-family: var(--font-display); font-weight: 600; font-size: 1.3rem; margin-bottom: 4px; }
.edu-major { color: var(--text-muted); margin-bottom: 18px; }
.edu-courses-label { display: block; font-size: 0.82rem; color: var(--text-dim); margin-bottom: 8px; font-family: var(--font-mono); }
.edu-courses p { color: var(--text-muted); font-size: 0.88rem; line-height: 1.75; }
.assess-text { color: var(--text-muted); line-height: 1.85; margin: 24px 0 24px; font-size: 0.98rem; }
.assess-traits { display: flex; flex-wrap: wrap; gap: 8px; }
.assess-tag { padding: 6px 14px; background: var(--bg-elevated); border: 1px solid var(--border-soft); border-radius: 100px; font-size: 0.82rem; color: var(--text); }

/* ===== 联系方式 ===== */
.contact-section { border-top: 1px solid var(--border-soft); }
.contact-grid { margin-top: 56px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.contact-card { display: flex; align-items: center; gap: 16px; background: var(--bg-surface); border: 1px solid var(--border-soft); border-radius: var(--radius); padding: 22px 24px; transition: all 0.25s ease; }
.contact-card:hover { border-color: var(--accent); transform: translateY(-3px); background: var(--bg-elevated); }
.contact-icon { width: 46px; height: 46px; display: grid; place-items: center; border-radius: 12px; background: var(--accent-soft); color: var(--accent); font-size: 1.2rem; flex-shrink: 0; }
.contact-text { flex: 1; min-width: 0; }
.contact-label { font-size: 0.78rem; color: var(--text-dim); margin-bottom: 2px; }
.contact-value { font-family: var(--font-mono); font-size: 0.92rem; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.contact-ext { color: var(--text-dim); font-size: 0.8rem; }

@media (max-width: 1024px) {
  .domain-grid { grid-template-columns: 1fr; }
  .skills-layout { grid-template-columns: 1fr; }
  .radar-box { position: static; }
  .skill-groups { grid-template-columns: repeat(2, 1fr); }
  .proj-list { grid-template-columns: 1fr; }
  .contact-grid { grid-template-columns: repeat(2, 1fr); }
  .r-stats-bar { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .r-hero-inner { grid-template-columns: 1fr; text-align: center; }
  .r-hero-left { margin: 0 auto; }
  .r-info-grid { grid-template-columns: 1fr; margin: 0 auto 26px; }
  .r-traits { justify-content: center; }
  .r-intro { margin: 0 auto 28px; }
  .skill-groups { grid-template-columns: 1fr; }
  .edu-grid { grid-template-columns: 1fr; }
  .contact-grid { grid-template-columns: 1fr; }
  .r-stats-bar { grid-template-columns: 1fr; }
}
</style>
